import React, { useState } from "react";
import { usePatient } from "../context/PatientContext";
import { User, AlertTriangle, Activity, Pin, Droplet, Edit2, Save, X } from "lucide-react";

export default function PatientBanner() {
  const { selectedPatient } = usePatient();

  if (!selectedPatient) return null;

  const hasAllergies = selectedPatient.allergies && selectedPatient.allergies.length > 0;

  // Clinically Calibrate Life Stage Classification
  const calculatedAge = selectedPatient.age 
    ? Number(selectedPatient.age) 
    : selectedPatient.dob 
      ? (new Date().getFullYear() - new Date(selectedPatient.dob).getFullYear()) 
      : 35;

  // EXPERT COMMENTARY: Determine suitable 3D avatar parameters based on patient age and gender.
  // Using 'avataaars' style for highly customizable, demographic-appropriate representations (Iraqi/Arab traits).
  const isFemale = (selectedPatient.gender || "").toLowerCase().includes("female") || (selectedPatient.gender || "").includes("أنثى") || (selectedPatient.gender || "").toLowerCase() === "f";
  
  let avatarStyle = "avataaars"; 
  let avatarSeed = `${selectedPatient.id || "default"}_${selectedPatient.name}`;
  let extraParams = "";

  const safeAge = Math.max(0, isNaN(calculatedAge) ? 35 : calculatedAge);

  // Iraqi/Arab regional traits
  const regionalSkin = "tanned,brown,light,pale,yellow"; 
  const regionalHair = "black,brown,brownDark";
  const elderHair = "silverGray,platinum";
  const clothingTypes = "blazerShirt,blazerSweater,collarSweater,shirtCrewNeck,shirtVNeck";

  if (safeAge < 1) {
    // Infant
    extraParams = `&top=noHair,shortHairShortFlat&hairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=shirtScoopNeck,shirtVNeck&eyes=happy,default,wink&mouth=smile,default`;
  } else if (safeAge < 3) {
    // Toddler
    extraParams = `&top=shortHairShortFlat,shortHairShortRound,shortHairFrizzle&hairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=overall,shirtCrewNeck&eyes=happy,default&mouth=smile`;
  } else if (safeAge < 12) {
    // Child
    if (isFemale) {
      extraParams = `&top=longHairStraight,longHairCurly,longHairNotTooLong&hairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default,happy`;
    } else {
      extraParams = `&top=shortHairShortFlat,shortHairShortWaved,shortHairShortRound&hairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default,happy`;
    }
  } else if (safeAge < 18) {
    // Teen
    if (isFemale) {
      extraParams = `&top=longHairStraight,longHairCurly,longHairNotTooLong,hijab&hairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default`;
    } else {
      extraParams = `&top=shortHairShortFlat,shortHairShortWaved,shortHairTheCaesar&hairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default`;
    }
  } else if (safeAge > 65) {
    // Geriatric (Seniors)
    if (isFemale) {
      // High probability of hijab for older Iraqi women
      extraParams = `&top=hijab,longHairStraightStrand&hairColor=${elderHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default&mouth=default`;
    } else {
      // Older men often have beards/mustaches and silver hair
      extraParams = `&top=noHair,shortHairSides&hairColor=${elderHair}&facialHair=beardMajestic,beardMedium,moustasheMagnum&facialHairProbability=80&facialHairColor=${elderHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default&mouth=default`;
    }
  } else {
    // Standard Adult (18 - 65)
    if (isFemale) {
      // Mix of hijab and standard hair for adult women
      extraParams = `&top=hijab,longHairStraight,longHairCurly,longHairNotTooLong&hairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default&mouth=default`;
    } else {
      // Mix of beards and clean shaven for adult men
      extraParams = `&top=shortHairShortFlat,shortHairShortWaved,shortHairTheCaesar&hairColor=${regionalHair}&facialHair=beardLight,beardMedium,moustasheMagnum&facialHairProbability=40&facialHairColor=${regionalHair}&skinColor=${regionalSkin}&clothing=${clothingTypes}&eyes=default&mouth=default`;
    }
  }

  // Construct absolute Dicebear 3D human-style SVG URL with a soft, clean background palette ensuring sandboxed visual safety and compliance
  const dicebearUrl = `https://api.dicebear.com/7.x/${avatarStyle}/svg?seed=${encodeURIComponent(avatarSeed)}&backgroundColor=cbd5e1,bae6fd,c0f2fe${extraParams}`;
  const avatarUrl = (selectedPatient as any).profileImageUrl || dicebearUrl;

  let stageLabel = "Adult";
  let stageColor = "text-sky-400 bg-sky-500/10 border-sky-500/20";
  let stageDesc = "Adult (بالغ)";
  let gaugePercent = 50;

  if (calculatedAge < 18) {
    stageLabel = "Pediatric";
    stageColor = "text-teal-400 bg-teal-500/10 border-teal-500/20";
    stageDesc = "Pediatric (أطفال)";
    gaugePercent = Math.min(100, Math.max(5, (calculatedAge / 17) * 33));
  } else if (calculatedAge <= 65) {
    stageLabel = "Adult";
    stageColor = "text-sky-400 bg-sky-500/10 border-sky-500/20";
    stageDesc = "Adult (بالغ)";
    gaugePercent = Math.min(100, Math.max(34, 34 + ((calculatedAge - 18) / (65 - 18)) * 32));
  } else {
    stageLabel = "Geriatric";
    stageColor = "text-amber-400 bg-amber-500/10 border-amber-500/20";
    stageDesc = "Geriatric (مسن)";
    gaugePercent = Math.min(100, Math.max(67, 67 + ((calculatedAge - 66) / 34) * 33));
  }

  return (
    <div className="bg-[#1E293B] text-white px-4 py-1 flex flex-col sm:flex-row sm:items-center justify-between text-[10px] font-medium sticky top-9 z-30 shadow-md gap-2 sm:gap-0">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3 border-r border-slate-600 pr-4">
          <Pin className="w-3 h-3 text-emerald-400 rotate-45 shrink-0" />
           {/* EXPERT COMMENTARY: Display a unique, generated patient 3D person avatar tailored dynamically to their age and gender.
              Includes robust error handling fallback and secure referrer policy to maintain UI consistency and visual polish under sandbox/iframe environments */}
          <div className="relative group cursor-pointer shrink-0 -mb-6 z-40">
            {/* Glowing ring animation */}
            <div className="absolute inset-0 bg-emerald-400/30 rounded-full animate-ping opacity-75 duration-1000"></div>
            
            <div className="relative w-14 h-14 rounded-full overflow-hidden bg-gradient-to-tr from-slate-700 to-slate-500 border-[3px] border-[#1E293B] flex items-center justify-center shadow-[0_4px_15px_rgba(0,0,0,0.5)] group-hover:scale-105 group-hover:shadow-[0_6px_20px_rgba(52,211,153,0.5)] transition-all duration-300 ring-2 ring-emerald-400 ring-offset-0">
              <img 
                src={avatarUrl}
                alt="Patient Profile Avatar"
                className="w-full h-full object-cover scale-[1.15] group-hover:scale-[1.25] transition-transform duration-500"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  // Securely fallback to a clean SVG silhouette icon in case of offline states or blocked referrer URLs
                  e.currentTarget.style.display = 'none';
                  const container = e.currentTarget.parentElement;
                  if (container) {
                    const fallback = container.querySelector('.avatar-offline-fallback');
                    if (fallback) fallback.classList.remove('hidden');
                  }
                }}
              />
              <div className="avatar-offline-fallback hidden absolute inset-0 flex items-center justify-center bg-gradient-to-tr from-slate-800 to-slate-600">
                <User className="w-5 h-5 text-emerald-400 drop-shadow-md" />
              </div>
            </div>
            
            {/* Online Status Indicator */}
            <div className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-emerald-500 border-[2px] border-[#1E293B] rounded-full shadow-sm z-10"></div>
          </div>
          <div className="flex items-center gap-1 group">
            <span className="font-bold text-xs tracking-wide">{selectedPatient.name}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 border-r border-slate-600 pr-4 text-slate-300">
          <div className="flex items-center gap-1 group">
            <span>{(selectedPatient.gender || "Unknown Gender").replace(/\s*font-\w+/g, '')}</span>
          </div>
          <span>&bull;</span>
          <div className="flex items-center gap-1 group">
            <span>{selectedPatient.dob} ({calculatedAge} yo)</span>
          </div>
          <span>&bull;</span>
          <div className="flex items-center gap-1 group">
            <span className="font-mono">{selectedPatient.phone || "No Phone"}</span>
          </div>
          <span>&bull;</span>
          <div className="flex items-center gap-1 group">
            <span className="font-mono text-[9px]">{selectedPatient.email || "No Email"}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 border-r border-slate-600 pr-4 text-slate-300">
          <div className="flex items-center gap-1 group">
            <span className="text-[10px] text-slate-400">💳 البطاقة:</span>
            <span className={selectedPatient.nationalId ? "font-bold text-white" : "text-amber-400 font-bold"}>
              {selectedPatient.nationalId || "بلا هوية"}
            </span>
          </div>
        </div>

        {/* Dynamic Patient Life-Stage Visual Gauge */}
        <div className="flex items-center gap-2 border-r border-slate-600 pr-4 select-none">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">Stage:</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold border ${stageColor}`}>
            {stageDesc}
          </span>
          <div className="relative w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden flex self-center">
            {/* Segment 1: Pediatric (<18 y.o. / Teal) */}
            <div className={`h-full w-1/3 transition-all ${calculatedAge < 18 ? 'bg-teal-400 opacity-100' : 'bg-teal-900/40 opacity-40'}`} title="Pediatric (<18)" />
            {/* Segment 2: Adult (18-65 y.o. / Sky) */}
            <div className={`h-full w-1/3 transition-all ${calculatedAge >= 18 && calculatedAge <= 65 ? 'bg-sky-400 opacity-100' : 'bg-sky-900/40 opacity-40'}`} title="Adult (18-65)" />
            {/* Segment 3: Geriatric (>65 y.o. / Amber) */}
            <div className={`h-full w-1/3 transition-all ${calculatedAge > 65 ? 'bg-amber-400 opacity-100' : 'bg-amber-900/40 opacity-40'}`} title="Geriatric (>65)" />
            
            {/* Indicator Dot */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-white shadow-md border border-slate-900 rounded-full transition-all duration-500"
              style={{ left: `${Math.min(95, Math.max(2, gaugePercent))}%` }}
            />
          </div>
        </div>

        {selectedPatient.bloodType && (
          <div className="flex items-center gap-1.5 text-rose-400 border-r border-slate-600 pr-4 font-bold">
            <Droplet className="w-3.5 h-3.5 fill-rose-500" />
            <span>Blood: {selectedPatient.bloodType}</span>
          </div>
        )}

        {hasAllergies && (
          <div className="flex items-center gap-2 text-rose-400">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="uppercase tracking-widest text-[9px] font-bold">Allergies: {selectedPatient.allergies?.join(', ')}</span>
          </div>
        )}
      </div>
      
      <div className="hidden md:flex items-center gap-1.5 text-emerald-400 bg-emerald-950/50 px-1.5 py-0.5 rounded">
        <Activity className="w-3 h-3" />
        <span className="text-[9px] tracking-widest uppercase font-bold">Pinned Context</span>
      </div>
    </div>
  );
}
